/**
 * A nova organização com contrato (#765, fase 4, #769).
 *
 * A criação passa a pedir cadastro, contrato e contato financeiro. O plano vem
 * do catálogo e traz os valores dele, que a pessoa pode mudar só para este
 * cliente. O primeiro administrador já conta como um dos usuários contratados.
 *
 * ── Quem o monta é a `NovaOrganizacao` ────────────────────────────────────
 *
 * Ele nasceu antes do back (decisão de 26/10/2026), e por isso recebe tudo
 * pelas props e devolve o pedido em `aoCriar`. A `NovaOrganizacao` cuida do
 * resto, como antes: o progresso, as perguntas (a conta que já existe e a
 * organização encerrada), o "Tentar de novo" com o identificador travado e o
 * resultado.
 *
 * ── O plano preenche, e a tela mostra o que preencheu ─────────────────────
 *
 * Escolhido o plano, os quatro campos do contrato recebem os valores dele e
 * acendem um de cada vez: a pessoa vê o que mudou sem ter digitado.
 */

import { useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { Botao, Campo, CampoEscolha, Icone, type OpcaoDeEscolha } from '@/shared/ui';
import { dinheiroSemCentavosQuandoRedondo, mascararReais } from '@/shared/lib/formato';
import { FUSOS_DO_BRASIL } from '@/shared/lib/fusos-do-brasil';
import { Faixa } from '@/shared/ui/estados/faixa';
import { cnpjSemMascara, cnpjValido, mascararCnpj } from '@/shared/lib/cnpj';
import {
  lerFormulario,
  valoresDoPlano,
  type CamposDoFormulario,
  type ErrosDoFormulario,
  type NovaOrganizacaoComContrato,
  type PlanoParaEscolher,
} from '../lib/formulario-com-contrato';
import { slugDoNome } from '../lib/regras-dos-campos';
import './formulario-com-contrato.css';

export type FormularioComContratoProps = {
  /** Só os planos ativos: o inativo não aparece para organização nova. */
  planos: readonly PlanoParaEscolher[];
  segmentos: readonly OpcaoDeEscolha[];
  /** O pedido foi para o servidor e a resposta ainda não voltou. */
  enviando: boolean;
  /** O motivo da recusa do servidor, já em frase. */
  falha?: string | null;
  aoCriar: (dados: NovaOrganizacaoComContrato) => void;
  /** Sem ele, não há o botão de cancelar. */
  aoCancelar?: () => void;
  /** A data de hoje, AAAA-MM-DD: o início padrão do contrato. */
  hoje: string;
  /**
   * A criação pode ter começado: o nome fantasia e o identificador ficam
   * travados, porque tentar de novo só continua com o MESMO identificador. O
   * CNPJ também: a criação que começou pode já tê-lo gravado.
   */
  travado?: boolean;
  /** O texto do botão de criar: "Tentar de novo", "Criar com esta conta". */
  rotuloDoEnvio?: string;
  /** O que a tela de fora quer dizer antes dos botões: a pergunta da conta, o que não terminou. */
  avisos?: ReactNode;
  /** Um botão a mais, ao lado do de criar: "Começar outra". */
  outraAcao?: ReactNode;
  /** Criar não serve agora, e o porquê: o botão fica desligado com ele. */
  bloqueado?: string | null;
  /** O e-mail do administrador mudou: a pergunta sobre o de antes não vale mais. */
  aoMudarEmailDoAdministrador?: () => void;
  /** O identificador mudou: a pergunta sobre a organização do de antes não vale mais. */
  aoMudarIdentificador?: () => void;
};

const FUSOS: OpcaoDeEscolha[] = FUSOS_DO_BRASIL.map((fuso) => ({ ...fuso }));

export function FormularioComContrato({
  planos,
  segmentos,
  enviando,
  falha = null,
  aoCriar,
  aoCancelar,
  hoje,
  travado = false,
  rotuloDoEnvio = 'Criar organização',
  avisos = null,
  outraAcao = null,
  bloqueado = null,
  aoMudarEmailDoAdministrador,
  aoMudarIdentificador,
}: FormularioComContratoProps): ReactNode {
  const [campos, definirCampos] = useState<CamposDoFormulario>({
    razaoSocial: '',
    nomeFantasia: '',
    cnpj: '',
    segmento: '',
    identificador: '',
    fuso: 'America/Sao_Paulo',
    planoId: '',
    usuarios: '',
    adicional: '',
    armazenamento: '',
    gbAdicional: '',
    inicio: hoje,
    nomeDoAdministrador: '',
    emailDoAdministrador: '',
    telefoneDoAdministrador: '',
    nomeDoFinanceiro: '',
    emailDoFinanceiro: '',
    telefoneDoFinanceiro: '',
  });
  // `false` enquanto a pessoa não mexe: o identificador acompanha o nome fantasia.
  const [identificadorTocado, definirIdentificadorTocado] = useState(false);
  // Cada escolha de plano acende os campos de novo: a `key` muda com ela.
  const [vezesDoPlano, definirVezesDoPlano] = useState(0);
  const [erros, definirErros] = useState<ErrosDoFormulario>({});

  const mudar = <C extends keyof CamposDoFormulario>(campo: C, valor: string) =>
    definirCampos((antes) => ({ ...antes, [campo]: valor }));

  const plano = planos.find((p) => p.id === campos.planoId) ?? null;
  const cnpjCompleto = cnpjSemMascara(campos.cnpj).length === 14;
  const avisoDoCnpj = cnpjCompleto && !cnpjValido(campos.cnpj);

  const escolherPlano = (id: string) => {
    const escolhido = planos.find((p) => p.id === id);
    if (!escolhido) return;
    definirCampos((antes) => ({ ...antes, planoId: id, ...valoresDoPlano(escolhido) }));
    definirVezesDoPlano((vezes) => vezes + 1);
  };

  const enviar = (evento: FormEvent) => {
    evento.preventDefault();
    const leitura = lerFormulario(campos);
    if (!leitura.ok) {
      definirErros(leitura.erros);
      return;
    }
    definirErros({});
    aoCriar(leitura.dados);
  };

  const erro = (campo: keyof CamposDoFormulario): string | undefined => erros[campo];
  const realce = (posicao: number) => ({
    realce: vezesDoPlano > 0,
    style: { '--i': posicao } as CSSProperties,
  });

  return (
    <form className="formulario-com-contrato" onSubmit={enviar} noValidate>
      <div className="formulario-com-contrato__grupos">
        <fieldset className="formulario-com-contrato__grupo" disabled={enviando}>
          <legend>A organização</legend>
          <div className="formulario-com-contrato__largo">
            <Campo
              rotulo="Razão social"
              value={campos.razaoSocial}
              erro={erro('razaoSocial')}
              onChange={(evento) => mudar('razaoSocial', evento.target.value)}
            />
          </div>
          <Campo
            rotulo="Nome fantasia"
            value={campos.nomeFantasia}
            erro={erro('nomeFantasia')}
            readOnly={travado}
            onChange={(evento) => {
              const nome = evento.target.value;
              definirCampos((antes) => ({
                ...antes,
                nomeFantasia: nome,
                ...(identificadorTocado ? {} : { identificador: slugDoNome(nome) }),
              }));
              // Enquanto ninguém mexe nele, o identificador muda junto com o nome.
              if (!identificadorTocado) aoMudarIdentificador?.();
            }}
          />
          <div className="formulario-com-contrato__cnpj">
            {/* Texto, e não só números: o CNPJ alfanumérico tem letras (IN RFB 2.229/2024). */}
            <Campo
              rotulo="CNPJ"
              autoCapitalize="characters"
              spellCheck={false}
              // Tentar de novo com outro CNPJ seria recusado: a organização que
              // começou a nascer já pode estar com este.
              readOnly={travado}
              {...(travado
                ? { ajuda: 'Travado: a criação que começou pode já ter gravado este CNPJ.' }
                : {})}
              value={campos.cnpj}
              erro={avisoDoCnpj ? 'Confira o CNPJ: os dígitos não batem.' : erro('cnpj')}
              onChange={(evento) => mudar('cnpj', mascararCnpj(evento.target.value))}
            />
            {cnpjCompleto && !avisoDoCnpj ? (
              <span className="formulario-com-contrato__ok">
                <Icone nome="check" />
                CNPJ válido
              </span>
            ) : null}
          </div>
          <CampoEscolha
            rotulo="Segmento"
            valor={campos.segmento}
            opcoes={segmentos}
            vazio="Escolha"
            erro={erro('segmento')}
            aoEscolher={(valor) => mudar('segmento', valor)}
          />
          <Campo
            rotulo="Identificador"
            ajuda={
              travado
                ? 'Travado: tentar de novo continua a criação com este identificador.'
                : 'Vai no endereço do login: minúsculas, números e hífen.'
            }
            value={campos.identificador}
            erro={erro('identificador')}
            readOnly={travado}
            spellCheck={false}
            onChange={(evento) => {
              definirIdentificadorTocado(true);
              mudar('identificador', evento.target.value.toLowerCase());
              aoMudarIdentificador?.();
            }}
          />
          <div className="formulario-com-contrato__largo">
            <CampoEscolha
              rotulo="Fuso horário"
              valor={campos.fuso}
              opcoes={FUSOS}
              erro={erro('fuso')}
              aoEscolher={(valor) => mudar('fuso', valor)}
            />
          </div>
        </fieldset>

        <fieldset className="formulario-com-contrato__grupo" disabled={enviando}>
          <legend>Contrato</legend>
          <div className="formulario-com-contrato__largo">
            <CampoEscolha
              rotulo="Plano"
              valor={campos.planoId}
              vazio="Escolha um plano do catálogo"
              opcoes={planos.map((p) => ({
                valor: p.id,
                rotulo: p.nome,
                detalhe: `${p.usuariosIncluidos} usuários · ${dinheiroSemCentavosQuandoRedondo(p.mensalidade) ?? 'sem mensalidade'}`,
              }))}
              erro={erro('planoId')}
              aoEscolher={escolherPlano}
            />
          </div>
          <Campo
            key={`usuarios-${vezesDoPlano}`}
            rotulo="Usuários contratados"
            inputMode="numeric"
            value={campos.usuarios}
            erro={erro('usuarios')}
            onChange={(evento) => mudar('usuarios', evento.target.value)}
            {...realce(0)}
          />
          <Campo
            key={`adicional-${vezesDoPlano}`}
            rotulo="Usuário adicional deste cliente"
            inputMode="decimal"
            value={campos.adicional}
            erro={erro('adicional')}
            onChange={(evento) => mudar('adicional', mascararReais(evento.target.value))}
            {...realce(1)}
          />
          <Campo
            key={`armazenamento-${vezesDoPlano}`}
            rotulo="Armazenamento contratado (GB)"
            inputMode="numeric"
            value={campos.armazenamento}
            erro={erro('armazenamento')}
            onChange={(evento) => mudar('armazenamento', evento.target.value)}
            {...realce(2)}
          />
          <Campo
            key={`gb-adicional-${vezesDoPlano}`}
            rotulo="GB adicional deste cliente"
            inputMode="decimal"
            value={campos.gbAdicional}
            erro={erro('gbAdicional')}
            onChange={(evento) => mudar('gbAdicional', mascararReais(evento.target.value))}
            {...realce(3)}
          />
          <Campo
            rotulo="Início do contrato"
            type="date"
            value={campos.inicio}
            erro={erro('inicio')}
            onChange={(evento) => mudar('inicio', evento.target.value)}
          />
          {plano ? (
            <p className="formulario-com-contrato__nota">
              Valores do plano {plano.nome}. Você pode mudar só para este cliente.
            </p>
          ) : null}
        </fieldset>

        <fieldset className="formulario-com-contrato__grupo" disabled={enviando}>
          <legend>Administrador</legend>
          <Campo
            rotulo="Nome"
            autoComplete="off"
            value={campos.nomeDoAdministrador}
            erro={erro('nomeDoAdministrador')}
            onChange={(evento) => mudar('nomeDoAdministrador', evento.target.value)}
          />
          <Campo
            rotulo="E-mail"
            type="email"
            autoComplete="off"
            value={campos.emailDoAdministrador}
            erro={erro('emailDoAdministrador')}
            onChange={(evento) => {
              mudar('emailDoAdministrador', evento.target.value);
              aoMudarEmailDoAdministrador?.();
            }}
          />
          <div className="formulario-com-contrato__largo">
            <Campo
              rotulo="Telefone (opcional)"
              type="tel"
              autoComplete="off"
              value={campos.telefoneDoAdministrador}
              erro={erro('telefoneDoAdministrador')}
              onChange={(evento) => mudar('telefoneDoAdministrador', evento.target.value)}
            />
          </div>
          <p className="formulario-com-contrato__nota formulario-com-contrato__largo">
            O primeiro administrador já conta como 1 dos usuários contratados.
          </p>
        </fieldset>

        <fieldset className="formulario-com-contrato__grupo" disabled={enviando}>
          <legend>Financeiro</legend>
          <div className="formulario-com-contrato__largo">
            <Campo
              rotulo="Nome do contato"
              autoComplete="off"
              value={campos.nomeDoFinanceiro}
              erro={erro('nomeDoFinanceiro')}
              onChange={(evento) => mudar('nomeDoFinanceiro', evento.target.value)}
            />
          </div>
          <Campo
            rotulo="E-mail"
            type="email"
            autoComplete="off"
            value={campos.emailDoFinanceiro}
            erro={erro('emailDoFinanceiro')}
            onChange={(evento) => mudar('emailDoFinanceiro', evento.target.value)}
          />
          <Campo
            rotulo="Telefone"
            type="tel"
            autoComplete="off"
            value={campos.telefoneDoFinanceiro}
            erro={erro('telefoneDoFinanceiro')}
            onChange={(evento) => mudar('telefoneDoFinanceiro', evento.target.value)}
          />
        </fieldset>
      </div>

      {Object.keys(erros).length > 0 ? (
        <Faixa tom="bloqueado">Corrija os campos marcados para criar a organização.</Faixa>
      ) : null}
      {avisos}
      {falha ? <Faixa tom="bloqueado">{falha}</Faixa> : null}

      <div className="formulario-com-contrato__acoes">
        {aoCancelar ? (
          <Botao desabilitado={enviando} motivo="Criando a organização" onClick={aoCancelar}>
            Cancelar
          </Botao>
        ) : null}
        {outraAcao}
        <Botao
          variante="primario"
          type="submit"
          desabilitado={enviando || bloqueado !== null}
          motivo={enviando ? 'Criando a organização' : (bloqueado ?? '')}
        >
          {enviando ? 'Criando… pode levar um minuto' : rotuloDoEnvio}
        </Botao>
      </div>
    </form>
  );
}
